/**
 * URL-prefix locale helpers for the public marketing site.
 *
 * Mirrors the backend's parseLangPrefix (graphql/helpers/langNegotiation.ts) so
 * both sides agree on the same 7 codes and the same "en" = no-prefix convention.
 */

export const MARKETING_LANG_CODES = ["en", "nl", "de", "fr", "es", "it", "pt"] as const;
export type MarketingLangCode = (typeof MARKETING_LANG_CODES)[number];

/**
 * `MarketingLangCode` minus "en" — the locales a feature page can actually graduate into (phase 3
 * step 2 of work/locale-coverage-unification.md in stippl-knowledge). English is never a key in
 * `FEATURE_PAGE_LOCALE_META` (`marketing/content/featurePageLocaleMeta.ts`): it already has its
 * own hand-written head built straight into each route, not a per-locale override of it.
 */
export type NonEnglishLocale = Exclude<MarketingLangCode, "en">;

/**
 * The locales Explore content exists in — now the full 7 above, same set as MARKETING_LANG_CODES.
 *
 * `/discover/*` is fed by the Explore documents, whose translation overlay (scripts/translateExplore.ts
 * in the backend repo) covered only nl/de/fr/es from 2026-08-26 to 2026-09-08, while it/pt sat in
 * `SUPPORTED_LANG_CODES`/`MARKETING_LANG_CODES` with no Explore content behind them — adding them
 * here first would have routed and hreflang'd English prose under an /it/ or /pt/ path. The
 * production translation pass completed 2026-09-08 (163 documents × 6 target locales, zero
 * failures), so all 7 now have real content.
 *
 * Mirrors EXPLORE_LOCALES in `graphql/helpers/langNegotiation.ts`, which carries the full order-
 * of-operations rule for the next locale addition — read it there before widening either list.
 * `graphql/helpers/langNegotiation.test.ts` asserts the two lists stay equal.
 */
export const EXPLORE_LOCALES = ["en", "nl", "de", "fr", "es", "it", "pt"] as const;
export type ExploreLocale = (typeof EXPLORE_LOCALES)[number];

export function isMarketingLangCode(value: string): value is MarketingLangCode {
	return (MARKETING_LANG_CODES as readonly string[]).includes(value);
}

export function isExploreLocale(value: string): value is ExploreLocale {
	return (EXPLORE_LOCALES as readonly string[]).includes(value);
}

/**
 * Narrows an i18next language tag to a marketing locale: "nl-NL" -> "nl", "PT-br" -> "pt", and
 * "en" for anything outside `MARKETING_LANG_CODES` (or no language at all). For app routes that
 * have no `/xx/` URL prefix to read the locale from and so take it from `i18n.language` instead.
 */
export function toMarketingLang(language: string | null | undefined): MarketingLangCode {
	const base = language?.split("-")[0]?.toLowerCase();
	return base && isMarketingLangCode(base) ? base : "en";
}

/**
 * The 16 marketing feature pages (the original 14 plus the ChatGPT and Claude landing pages), now real per-locale routes (phase 3 step 1 of
 * work/locale-coverage-unification.md in stippl-knowledge) — `noindex, follow` until each locale
 * gets hand-written title/meta, see the generator's doc comment in `marketingRoutes.tsx` for why.
 *
 * Single source of truth for `hasLocalisedRoute` below and for the localised route entries
 * `marketingRoutes.tsx` generates from it. Mirrored by hand as `LOCALISED_FEATURE_PATHS` in
 * `graphql/helpers/localisedRoutes.ts` — the server can't import this file (see that module's
 * doc comment) — and `graphql/helpers/localisedRoutes.test.ts` asserts the two lists are equal,
 * the same guard that already covers `hasLocalisedRoute` itself.
 */
export const LOCALISED_FEATURE_PATHS = [
	"/itinerary-planner",
	"/ai-travel-planner",
	"/budget-planner",
	"/expense-tracker",
	"/packing-list",
	"/your-map",
	"/social-travel-platform",
	"/travel-journal",
	"/travel-tracker",
	"/travel-reel",
	"/scratch-map",
	"/esim",
	"/pro",
	"/photobook",
	"/chatgpt-travel-planner",
	"/claude-travel-planner",
] as const;
export type LocalisedFeaturePath = (typeof LOCALISED_FEATURE_PATHS)[number];

/**
 * Splits a leading /xx/ locale prefix off a pathname, e.g. "/nl/about" -> { lang: "nl", basePath: "/about" }.
 * Paths with no recognised prefix resolve to "en" and are returned unchanged.
 */
export function stripLangPrefix(pathname: string): { lang: MarketingLangCode; basePath: string } {
	const match = pathname.match(/^\/([a-z]{2})(\/.*|$)/);
	if (match && isMarketingLangCode(match[1])) {
		return { lang: match[1], basePath: match[2] || "/" };
	}
	return { lang: "en", basePath: pathname };
}

/**
 * Rebuilds a pathname for the given language, e.g. ("/nl/about", "de") -> "/de/about".
 *
 * The locale root is the one shape that can't just be `prefix + basePath`: `basePath` for the
 * homepage is `"/"`, so naively concatenating gave `"/nl" + "/"` = `"/nl/"` — a trailing slash
 * that `<link rel="canonical">`, the hreflang cluster (`buildHomeHreflang`,
 * `marketing/seo/metaUtils.ts`) and the sitemap never emit, because none of them build a URL by
 * gluing a prefix onto `"/"` this way. Every internal link this function feeds (the language
 * switcher's `buildUrl`, `discoverPath`, `localizeHref`) pointed at that non-canonical `/nl/`
 * while every crawl signal pointed at `/nl` — so `/nl` and `/nl/` both served ~200 with near-
 * identical HTML and split as two rows in GSC/Fathom (123yb6937h2). Fixed by special-casing
 * `basePath === "/"` and dropping it instead of appending it. English must still return `"/"`
 * here, not `""` — an empty string is not a path — which `prefix || "/"` gives for free since an
 * empty `prefix` is falsy. Every other `basePath` (e.g. "/about") is unaffected: `("/about", "nl")`
 * is still `"/nl/about"`.
 */
export function buildLocalizedPath(pathname: string, lang: MarketingLangCode): string {
	const { basePath } = stripLangPrefix(pathname);
	const prefix = lang === "en" ? "" : `/${lang}`;
	if (basePath === "/") return prefix || "/";
	return `${prefix}${basePath}`;
}

/**
 * Whether a locale-prefixed route actually exists for `path` — i.e. whether prefixing it with
 * `/xx/` reaches a real SSR'd page instead of falling through to the SPA shell.
 *
 * That distinction is not cosmetic. An unrouted locale URL still returns HTTP 200 — a ~10.7 kB
 * shell with no `<h1>`, the generic homepage `<title>`, and no `noindex` — so Google indexes it
 * as a near-duplicate of the homepage. Measured on production 2026-09-10, before the 14 feature
 * pages got their own locale routes (phase 3 step 1, work/locale-coverage-unification.md):
 *
 *   /nl/                                real page,  244 kB   -> true
 *   /nl/blog                            real page,  652 kB   -> true
 *   /nl/blog/10-day-thailand-itinerary  real page,  128 kB   -> true
 *   /nl/discover/thailand               real page,  855 kB   -> true
 *   /nl/discover                        was an empty shell, 10.7 kB -> was FALSE (the index, not
 *                                        a country); 123yb6937h0 gave it a real SSR'd page — now true
 *   /nl/pricing, /nl/about, /nl/contact,
 *     /nl/for-business                  empty shell, 10.7 kB -> false
 *   all 14 /nl/<feature> pages
 *     (itinerary-planner, esim, ...)    were an empty shell, 10.7 kB -> now true (generated,
 *                                        `noindex, follow` — see the generator's doc comment in
 *                                        marketingRoutes.tsx for why)
 *
 * True for exactly:
 *   - "/"                                  — HOME_ROUTES, marketingRoutes.tsx (~line 351)
 *   - "/blog" and "/blog/<anything>"       — the seven per-locale blog routes, marketingRoutes.tsx
 *   - "/discover"                          — the seven per-locale hub routes, marketingRoutes.tsx
 *     (123yb6937h0 gave the bare index its own SSR'd page and locale siblings — it used to be
 *     the one deliberate exception in this table, an unrouted shell like every /<feature-page>
 *     below; it no longer is, and the language switcher (`LanguageSwitcher.tsx`) depends on this
 *     answer flipping to build `/nl/discover` etc. instead of falling back to the locale root)
 *   - "/discover/<country>"                — DISCOVER_GUIDE_ROUTES, marketingRoutes.tsx (~line 298)
 *   - "/discover/<country>/<destination>"  — DISCOVER_GUIDE_ROUTES, marketingRoutes.tsx (~line 298)
 *   - any of the 14 `LOCALISED_FEATURE_PATHS` above (itinerary-planner, esim, pro, ...) — the
 *     generated `noindex, follow` routes `marketingRoutes.tsx` builds from this same list; a
 *     sub-path (`/esim/foo`) does not count, only an exact match
 * False for everything else.
 *
 * This deliberately doesn't import the route table itself: `marketingRoutes.tsx` pulls in every
 * marketing page component, which is import weight this file — pulled into the client nav and
 * the language switcher — shouldn't carry, and it would make `i18n/urlLocale.ts` depend on the
 * module that already depends on `i18n/urlLocale.ts` (`EXPLORE_LOCALES`/`MARKETING_LANG_CODES`).
 * So the list above is kept by hand instead. If you add a new locale-prefixed SSR route, add it
 * in `marketingRoutes.tsx` (or extend the discover suffix pattern) AND here — this is the one
 * other place that needs to agree, and the next locale-route addition should find it here.
 *
 * Mirrored on the backend as `hasLocalisedRoute` in `graphql/helpers/localisedRoutes.ts` — the
 * server can't import this file (see that module's doc comment), so it carries its own copy of
 * this same logic, used to 301 a locale-prefixed URL that has no real route back to its
 * unprefixed form. `graphql/helpers/localisedRoutes.test.ts` imports this file directly and
 * asserts the two answer identically, so the next locale-route addition needs to land here AND
 * there — the test is what makes that non-optional.
 */
export function hasLocalisedRoute(path: string): boolean {
	// Trailing slash tolerated: the language switcher reads `window.location.pathname`, and a
	// visitor who lands on `/nl/discover/thailand/` would otherwise be told that page has no
	// localised route and offered the locale root instead of the page they are on.
	const stripped = stripLangPrefix(path).basePath;
	const basePath = stripped.length > 1 ? stripped.replace(/\/+$/, "") || "/" : stripped;
	if (basePath === "/") return true;
	if (basePath === "/blog" || basePath.startsWith("/blog/")) return true;
	// The bare hub, and every country/destination guide beneath it — see the doc comment above
	// for why "/discover" alone joined this list (123yb6937h0).
	if (basePath === "/discover") return true;
	if (/^\/discover\/[^/]+(\/[^/]+)?$/.test(basePath)) return true;
	if ((LOCALISED_FEATURE_PATHS as readonly string[]).includes(basePath)) return true;
	return false;
}

/**
 * Prefixes a /discover path with the given locale, the way every /discover link on the site
 * should be built — see the note above `EXPLORE_LOCALES`.
 *
 * `buildLocalizedPath` alone isn't enough here: it's keyed on `MARKETING_LANG_CODES`, not
 * `EXPLORE_LOCALES`, so passing it a marketing locale Explore has no route for (the state
 * it/pt were briefly in, and whatever the next added locale will be in again) would build a
 * prefix `DISCOVER_GUIDE_ROUTES` never registers — a link that renders but 404s. Clamping to
 * `EXPLORE_LOCALES` and falling back to English means that degrades to a working English page
 * instead of a dead one.
 */
export function discoverPath(path: string, lang: string): string {
	const safeLang = isExploreLocale(lang) ? lang : "en";
	return buildLocalizedPath(path, safeLang);
}

/**
 * Locale-prefix an href that came from DATA rather than from a component.
 *
 * Article bodies and breadcrumbs carry `/discover/...` URLs written by the knowledge base, which
 * stores them unprefixed on purpose — `build_article_documents.py`'s `_section_geo` docstring:
 * "Attributes are not translated, so that href is identical in all seven locales." So a German
 * article linked a German reader into the English hub.
 *
 * Everything that is not an internal path is returned untouched: an absolute URL, a `mailto:`,
 * an anchor, and a protocol-relative `//host` (which `startsWith("/")` alone would wrongly
 * accept). `hasLocalisedRoute` decides the rest — `/discover` and `/discover/<country>` both
 * get the prefix now that the bare hub has its own locale routes too (123yb6937h0).
 */
export function localizeHref(href: string, lang: MarketingLangCode = "en"): string {
	if (!href || !href.startsWith("/") || href.startsWith("//")) return href;
	return hasLocalisedRoute(href) ? buildLocalizedPath(href, lang) : href;
}
